import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { parseMeetingStartLink } from "@fg/ui-theme";
import { Loader2, MessagesSquare, ShieldAlert, UserX } from "@fg/icons";
import { ConnectApiError, connectParityApi } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { Button } from "@/components/ui/button";
import { placeCall } from "@/lib/calls";

type Failure =
    | { kind: "bad-link" }
    | { kind: "not-licensed"; detail: string }
    | { kind: "unknown-people"; people: string[] }
    | { kind: "error"; message: string };

/**
 * FPT-8: where other apps hand over to Meeting. Mail's Chat and Call actions
 * open `/start/chat?with=…` or `/start/call?with=…&media=…` (format in
 * @fg/ui-theme meeting-links). This page opens the chat with exactly those
 * people — creating it on first use — and for a call rings them from it.
 * The chat replaces this page in history, so Back never repeats a call.
 */
export function StartFromLinkPage() {
    const { action } = useParams<{ action: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const token = useAccessToken();
    const [failure, setFailure] = useState<Failure | null>(null);
    const [attempt, setAttempt] = useState(0);
    // One run per attempt — React's dev double-effect must not start two calls.
    const ran = useRef(-1);

    const link = parseMeetingStartLink(action, location.search);

    const start = useCallback(async () => {
        if (!link) {
            setFailure({ kind: "bad-link" });
            return;
        }
        setFailure(null);
        try {
            const chat = await connectParityApi.openChat(link.with, link.topic, token);
            const chatUrl = `/?view=dms&chat=${chat.kind}:${encodeURIComponent(chat.id)}`;
            navigate(chatUrl, { replace: true });
            if (link.action !== "call") return;
            const invitees = chat.people.map((p) => p.email).filter(Boolean);
            const label = link.media === "video" ? "Video" : "Audio";
            await placeCall({
                kind: link.media,
                subject: chat.kind === "dm" ? `${label} call with ${chat.title}` : `${label} call — ${chat.title}`,
                invitees,
                calleeLabel: chat.title,
                token,
                navigate
            });
        } catch (err) {
            if (err instanceof ConnectApiError && err.status === 403) {
                setFailure({ kind: "not-licensed", detail: err.message });
            } else if (err instanceof ConnectApiError && err.status === 404 && err.unresolved?.length) {
                setFailure({ kind: "unknown-people", people: err.unresolved });
            } else {
                setFailure({ kind: "error", message: err instanceof Error ? err.message : String(err) });
            }
        }
        // `link` is derived from the URL, which is what `location.search` tracks.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action, location.search, token, navigate]);

    useEffect(() => {
        if (!token || ran.current === attempt) return;
        ran.current = attempt;
        void start();
    }, [token, attempt, start]);

    if (!failure) {
        return (
            <Centered>
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
                <p className="text-sm text-muted-foreground" role="status">
                    {link?.action === "call" ? "Starting the call…" : "Opening the chat…"}
                </p>
            </Centered>
        );
    }

    const goToChat = () => navigate("/?view=dms", { replace: true });

    if (failure.kind === "not-licensed") {
        return (
            <Centered>
                <ShieldAlert className="h-10 w-10 text-amber-500" aria-hidden />
                <h1 className="text-lg font-semibold">Meeting isn't included in your account</h1>
                <p className="max-w-sm text-center text-sm text-muted-foreground">
                    Chats and calls happen in Meeting. Ask your administrator for a Meeting licence.
                </p>
                <p className="max-w-sm text-center text-xs text-muted-foreground">{failure.detail}</p>
            </Centered>
        );
    }

    if (failure.kind === "unknown-people") {
        return (
            <Centered>
                <UserX className="h-10 w-10 text-amber-500" aria-hidden />
                <h1 className="text-lg font-semibold">
                    {failure.people.length === 1 ? "This person isn't in your directory" : "Some people aren't in your directory"}
                </h1>
                <p className="max-w-sm text-center text-sm text-muted-foreground">
                    You can chat and call with people in your organisation, and in organisations yours works with.
                </p>
                <ul className="max-w-sm text-center text-sm">
                    {failure.people.map((p) => <li key={p} className="break-all">{p}</li>)}
                </ul>
                <Button variant="outline" onClick={goToChat}>Go to Chat</Button>
            </Centered>
        );
    }

    return (
        <Centered>
            <MessagesSquare className="h-10 w-10 text-muted-foreground" aria-hidden />
            <h1 className="text-lg font-semibold">
                {failure.kind === "bad-link" ? "This link doesn't name anyone" : "Couldn't open the chat"}
            </h1>
            {failure.kind === "error" ? (
                <p className="max-w-sm text-center text-sm text-muted-foreground">{failure.message}</p>
            ) : null}
            <div className="flex gap-2">
                {failure.kind === "error" ? <Button onClick={() => setAttempt((n) => n + 1)}>Try again</Button> : null}
                <Button variant="outline" onClick={goToChat}>Go to Chat</Button>
            </div>
        </Centered>
    );
}

function Centered({ children }: { children: ReactNode }) {
    return (
        <main className="flex min-h-[100dvh] flex-col items-center justify-center gap-4 bg-background p-6 text-foreground">
            {children}
        </main>
    );
}
